import { isEqual } from 'lodash';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Action } from '~/eds/src/types/Action';
import { Filter } from '~/evifields/src/types/Filter';
import { slices } from '~/redux/slices';
import { Chart, CrossFilter } from '~/types/DashboardV2';

import { toFilter } from './Dashboard.utils';

export const useCharts = () => {
  const dispatch = useDispatch();

  const charts = useSelector(slices.dashboardV2.selectors.selectCharts);
  const intervals = useSelector(slices.dashboardV2.selectors.selectIntervals);
  const chartWidths = useSelector(
    slices.dashboardV2.selectors.selectChartWidths,
  );
  const crossFilters = useSelector(
    slices.dashboardV2.selectors.selectCrossFilters,
  );
  const currentChart = useSelector(
    slices.dashboardV2.selectors.selectCurrentChart,
  );

  const handleRearrangeCharts = useCallback(
    (updatedCharts: Chart[]) => {
      if (
        !isEqual(
          charts.map((c) => c.id),
          updatedCharts.map((c) => c.id),
        )
      ) {
        dispatch(slices.dashboardV2.actions.setDashboardDirty(true));
      }
      dispatch(slices.dashboardV2.actions.setChartFilters(updatedCharts));
    },
    [dispatch, charts],
  );

  const handleCrossFiltersChange = useCallback(
    (filters: Filter[]) => {
      dispatch(slices.dashboardV2.actions.setCrossFilters(filters));
    },
    [dispatch],
  );

  const handleSelectCrossFilter = useCallback(
    (crossFilters: CrossFilter[], fieldId: string) => {
      const newFilters = crossFilters.map((crossFilter) =>
        toFilter(crossFilter.fieldId, crossFilter.value!, crossFilter.type),
      );
      dispatch(slices.dashboardV2.actions.setCrossFilters(newFilters));
      dispatch(slices.dashboardV2.actions.setCurrentChart(fieldId));
    },
    [dispatch],
  );

  const onIntervalChange = useCallback(
    (action: Action<string>, filter: Chart) => {
      dispatch(
        slices.dashboardV2.actions.setChartInterval({
          id: filter.id,
          value: action.value,
        }),
      );
    },
    [dispatch],
  );

  const onChartWidthChange = useCallback(
    (filter: Chart, newWidth: string) => {
      dispatch(
        slices.dashboardV2.actions.setChartWidth({
          id: filter.id,
          value: newWidth,
        }),
      );
    },
    [dispatch],
  );

  const onChartTypeChange = useCallback(
    (filter: Chart, newType: string) => {
      dispatch(
        slices.dashboardV2.actions.setChartType({
          id: filter.id,
          value: newType,
        }),
      );
    },
    [dispatch],
  );

  const onRemoveItem = useCallback(
    (filter: Chart) => {
      dispatch(slices.dashboardV2.actions.deleteChart(filter));
    },
    [dispatch],
  );

  return {
    charts,
    intervals,
    chartWidths,
    crossFilters,
    currentChart,
    handleRearrangeCharts,
    handleCrossFiltersChange,
    handleSelectCrossFilter,
    onIntervalChange,
    onChartWidthChange,
    onChartTypeChange,
    onRemoveItem,
  };
};
